import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { followUpAPI, dashboardAPI, notificationSettingsAPI, dashboardDetailsAPI, paymentAPI } from '../api';
import { checkAndFireNotifications, checkAndFireDelayedNudges, requestPermission, getPermission } from '../lib/notificationService';
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  Settings,
  UserCog,
  Bell,
  Search,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Kanban,
  Wrench,
  Shield,
  AlertCircle,
  Clock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Target,
  Activity,
  Compass,
  Ruler,
  Trash2,
  GitBranch,
  Tags,
  DollarSign,
  Briefcase,
  TrendingUp,
  Wallet,
  Building2,
  Upload,
  PhoneCall,
  Zap,
  ClipboardCheck,
  Plus
} from 'lucide-react';
import { SIDEBAR } from '../constants/testIds';

export const CALL_INTELLIGENCE_PERMS = ['call_intelligence.view_self', 'call_intelligence.view_team', 'call_intelligence.view_all', 'call.analytics.view', 'call.view_all', 'call.view_team', 'call.view_own'];

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifData, setNotifData] = useState({ missed: [], today: [], missed_count: 0, today_count: 0 });
  const [payReminders, setPayReminders] = useState({ due_today: [], overdue: [], counts: { due_today: 0, overdue: 0 } });
  const [searchTerm, setSearchTerm] = useState('');
  const [quickOpen, setQuickOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { user, logout, hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const notifRef = useRef(null);
  const quickRef = useRef(null);
  const profileRef = useRef(null);

  const isSuperAdmin = user?.role === 'super_admin';
  const isArchitect = user?.role === 'architect';
  const isArchitectManager = user?.role === 'architect_manager';
  // Both architect-namespace roles are API-firewalled to /api/architect/* — same chrome behaviour.
  const inArchitectWorkspace = user?.role === 'architect' || user?.role === 'architect_manager';

  const standardNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, testId: SIDEBAR.dashboardLink, perm: 'dashboard.view' },
    { name: 'Leads', href: '/leads', icon: Users, testId: SIDEBAR.leadsLink, perm: 'leads.view' },
    { name: 'Pipeline', href: '/pipeline', icon: Kanban, testId: SIDEBAR.pipelineLink, perm: 'leads.view' },
    { name: 'Project Workflows', href: '/workflows', icon: GitBranch, testId: 'sidebar-workflows-link', perm: 'leads.view' },
    { name: 'Architect', href: '/architect', icon: Compass, testId: 'sidebar-architect-link', perm: null, roles: ['architect', 'architect_manager', 'super_admin'] },
    { name: 'Sales Targets', href: '/targets', icon: Target, testId: 'sidebar-targets-link', perm: 'targets.view' },
    { name: 'Performance', href: '/performance', icon: Activity, testId: 'sidebar-performance-link', perm: 'reports.view' },
    { name: "Today's Work", href: '/work-queue', icon: Clock, testId: 'sidebar-workqueue-link', perm: 'leads.view' },
    { name: 'My Business', href: '/my-business', icon: Briefcase, testId: 'sidebar-my-business-link', perm: 'reports.view' },
    { name: 'Re-Enquiries', href: '/re-enquiries', icon: Users, testId: 'sidebar-reenquiries-link', perm: 'leads.view' },
    { name: 'Lead Approvals', href: '/lead-approvals', icon: ClipboardCheck, testId: 'sidebar-lead-approvals-link', perm: 'leads.assign' },
    { name: 'Assignment Intel', href: '/assignment-intelligence', icon: Target, testId: 'sidebar-assignment-link', perm: 'leads.assign' },
    { name: 'All Projects', href: '/projects', icon: GitBranch, testId: 'sidebar-projects-link', perm: 'leads.view' },
    { name: 'Clients', href: '/clients', icon: Users, testId: 'sidebar-clients-link', perm: 'clients.view' },
    { name: 'B2B Partners', href: '/b2b-partners', icon: Users, testId: 'sidebar-b2b-link', perm: 'b2b.view' },
    { name: 'Marketing Intel', href: '/analytics/lead-performance', icon: TrendingUp, testId: 'sidebar-analytics-link', perm: 'analytics.view' },
    { name: 'Finance', href: '/finance', icon: Wallet, testId: 'sidebar-finance-link', perm: 'finance.view' },
    { name: 'Bulk Import', href: '/import', icon: Upload, testId: 'sidebar-import-link', perm: 'import.manage' },
    { name: 'My Calls', href: '/my-calls', icon: PhoneCall, testId: 'sidebar-mycalls-link', perm: 'call.view_own' },
    { name: 'Call Intelligence', href: '/call-intelligence', icon: Zap, testId: 'sidebar-call-intel-link', perm: null, anyPerm: CALL_INTELLIGENCE_PERMS },
    { name: 'Calendar', href: '/calendar', icon: Calendar, testId: SIDEBAR.calendarLink, perm: 'calendar.view' },
    { name: 'Users', href: '/users', icon: UserCog, testId: SIDEBAR.usersLink, perm: 'users.view' },
    { name: 'Roles & Permissions', href: '/roles', icon: Shield, testId: SIDEBAR.rolesLink, perm: null, superAdminOnly: true },
    { name: 'Services', href: '/services', icon: Wrench, testId: SIDEBAR.servicesLink, perm: null, superAdminOnly: true },
    { name: 'Lead Statuses', href: '/lead-statuses', icon: Tags, testId: 'sidebar-lead-statuses-link', perm: null, superAdminOnly: true },
    { name: 'Activity Log', href: '/activity', icon: FileText, testId: 'sidebar-activity-link', perm: 'activity.view' },
    { name: 'Field Visibility', href: '/field-visibility', icon: Shield, testId: 'sidebar-field-visibility-link', perm: null, superAdminOnly: true },
    { name: 'Recycle Bin', href: '/recycle-bin', icon: Trash2, testId: 'sidebar-recycle-bin-link', perm: null, superAdminOnly: true },
    { name: 'Settings', href: '/settings', icon: Settings, testId: SIDEBAR.settingsLink, perm: 'settings.view' }
  ];

  const allowed = (item) => {
    if (item.superAdminOnly) return isSuperAdmin;
    if (item.roles) return item.roles.includes(user?.role);
    if (item.href === '/my-calls' && (user?.calling_controls?.my_calls_access === false)) return false;
    if (item.anyPerm) return hasAnyPermission(item.anyPerm);
    return item.perm ? hasPermission(item.perm) : true;
  };

  const navigation = isArchitect ? [
    { name: 'Architect Dashboard', href: '/architect', icon: Compass, testId: 'sidebar-architect-link' }
  ] : isArchitectManager ? [
    { name: 'Manager Dashboard', href: '/architect', icon: Compass, testId: 'sidebar-architect-manager-link' },
    { name: 'My Workplace', href: '/architect/workspace', icon: Ruler, testId: 'sidebar-architect-workspace-link' }
  ] : standardNavigation.filter(allowed);

  const reportsNav = !isArchitect && !isArchitectManager && hasPermission('reports.view') ? [
    { name: 'Daily Progress Report', href: '/reports/dpr', icon: FileText, testId: 'sidebar-reports-dpr-link' },
    { name: 'Sales Report', href: '/reports/sales', icon: FileText, testId: 'sidebar-reports-sales-link' },
    { name: 'Lead Report', href: '/reports/leads', icon: FileText, testId: 'sidebar-reports-leads-link' }
  ] : [];

  // Presentational grouping only — every item reuses its existing route/permission definition.
  const pick = (...names) => names
    .map((n) => navigation.find((i) => i.name === n))
    .filter(Boolean);

  const navGroups = inArchitectWorkspace
    ? [{ label: null, items: navigation }]
    : [
      { label: null, items: pick('Dashboard') },
      { label: 'Sales & Marketing', items: pick('Leads', 'Pipeline', 'Re-Enquiries', 'Marketing Intel', 'Sales Targets') },
      { label: 'Projects & Operations', items: pick('All Projects', 'Clients', 'Project Workflows', 'Architect', 'Services', 'B2B Partners') },
      { label: 'Finance', items: pick('Finance', 'Bulk Import') },
      { label: 'Team & Productivity', items: pick("Today's Work", 'My Business', 'Calendar', 'My Calls', 'Call Intelligence', 'Performance') },
      { label: 'Admin & Settings', items: pick('Users', 'Roles & Permissions', 'Lead Approvals', 'Assignment Intel', 'Lead Statuses', 'Field Visibility', 'Activity Log', 'Recycle Bin', 'Settings') },
      { label: 'Reports', items: reportsNav },
    ].filter((g) => g.items.length > 0);

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const fetchNotifications = useCallback(async () => {
    if (!user || inArchitectWorkspace) return;
    try {
      const [missedRes, todayRes, upcomingRes, globalSettingRes, delayedRes] = await Promise.all([
        followUpAPI.getAll({ filter_type: 'missed' }),
        followUpAPI.getAll({ filter_type: 'today' }),
        followUpAPI.getAll({ filter_type: 'upcoming' }),
        notificationSettingsAPI.getGlobal().catch(() => ({ data: { enabled: true } })),
        dashboardDetailsAPI.delayedProjects().catch(() => ({ data: null }))
      ]);
      setNotifData({
        missed: missedRes.data.slice(0, 5),
        today: todayRes.data.slice(0, 5),
        missed_count: missedRes.data.length,
        today_count: todayRes.data.length
      });

      // Payment due/overdue reminders (only for users permitted to view payments)
      if (hasPermission && hasPermission('payment.view')) {
        try {
          const payRes = await paymentAPI.reminders();
          setPayReminders(payRes.data || { due_today: [], overdue: [], counts: { due_today: 0, overdue: 0 } });
        } catch (e) { /* not permitted / silent */ }
      }

      // Fire browser push notifications for follow-ups that just became due
      const globalEnabled = globalSettingRes.data?.enabled !== false;
      const userEnabled = user?.notifications_enabled !== false;
      const dueCandidates = [...todayRes.data, ...upcomingRes.data];
      checkAndFireNotifications(dueCandidates, {
        globalEnabled,
        userEnabled,
        onClick: (leadId) => navigate(`/leads/${leadId}`)
      });
      // Auto-nudge for newly-delayed projects (one notification per project per session)
      checkAndFireDelayedNudges(delayedRes.data, {
        globalEnabled,
        userEnabled,
        onClick: (leadId) => navigate(`/leads/${leadId}`)
      });
    } catch (e) {
      // Silent
    }
  }, [user, inArchitectWorkspace, navigate, hasPermission]);

  // Single immediate notification fetch + the existing 60s poll. The callback is held in a
  // ref so identity changes (user/navigate/hasPermission) can no longer re-fire the whole
  // notification bundle on every dashboard load.
  const fetchRef = useRef(fetchNotifications);
  const lastFetchAt = useRef(0);
  useEffect(() => { fetchRef.current = fetchNotifications; }, [fetchNotifications]);

  useEffect(() => {
    if (inArchitectWorkspace) return undefined;
    const run = () => {
      if (Date.now() - lastFetchAt.current < 5000) return;
      lastFetchAt.current = Date.now();
      fetchRef.current();
    };
    run();
    const interval = setInterval(run, 60000); // Poll every 60s (pre-existing behaviour)
    return () => clearInterval(interval);
  }, [inArchitectWorkspace]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
      if (quickRef.current && !quickRef.current.contains(event.target)) {
        setQuickOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/leads?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const totalNotifCount = notifData.missed_count + notifData.today_count + (payReminders.counts?.due_today || 0) + (payReminders.counts?.overdue || 0);
  const money = (v) => (v == null ? '\u2014' : `\u20b9${Number(v).toLocaleString('en-IN')}`);

  return (
    <div className="min-h-screen bg-background" data-testid={SIDEBAR.container}>
      {/* Sidebar - Desktop (light theme, grouped) */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col z-50">
        <div className="flex grow flex-col overflow-y-auto border-r border-slate-200 bg-white px-3 py-4 dark:border-slate-800 dark:bg-slate-950">
          <div className="shrink-0 px-2 pb-4">
            <img src="/gnons-logo.png" alt="GNONS Design & Construction" className="h-auto w-full max-h-11 object-contain object-left" data-testid="sidebar-logo" />
          </div>
          <nav className="flex flex-1 flex-col">
            <div className="flex-1 space-y-4">
              {navGroups.map((group, gi) => (
                <div key={group.label || `group-${gi}`} className={gi > 0 ? 'border-t border-slate-100 pt-3 dark:border-slate-800' : ''}>
                  {group.label && (
                    <p
                      data-testid={group.label === 'Reports' ? SIDEBAR.reportsLink : `sidebar-group-${group.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400"
                    >
                      {group.label}
                    </p>
                  )}
                  <ul className="space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = item.icon || FileText;
                      return (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            data-testid={item.testId}
                            className={`group flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                              isActive(item.href)
                                ? 'bg-amber-500 text-white shadow-sm'
                                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                            }`}
                          >
                            <Icon className="h-[18px] w-[18px] shrink-0" />
                            <span className="truncate">{item.name}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-4 border-t border-slate-100 pt-3 dark:border-slate-800">
              <div className="flex items-center gap-x-3 px-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-700">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{user?.name}</p>
                  <p className="truncate text-xs capitalize text-slate-500">{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="sidebar-logout-btn"
                className="mt-2 flex w-full items-center justify-center gap-x-2 rounded-lg py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </nav>
        </div>
      </aside>

      {/* Mobile sidebar (same grouped structure, light theme) */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setSidebarOpen(false)}>
          <aside className="fixed inset-y-0 left-0 w-72 max-w-[85vw] overflow-y-auto bg-white p-3 dark:bg-slate-950" onClick={(e) => e.stopPropagation()} data-testid="mobile-sidebar">
            <div className="mb-4 flex items-center gap-3">
              <img src="/gnons-logo.png" alt="GNONS Design & Construction" className="h-auto min-w-0 flex-1 max-h-10 object-contain object-left" data-testid="mobile-sidebar-logo" />
              <button onClick={() => setSidebarOpen(false)} data-testid="mobile-sidebar-close" className="shrink-0 text-slate-500 hover:text-slate-900">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="space-y-4">
              {navGroups.map((group, gi) => (
                <div key={group.label || `mgroup-${gi}`} className={gi > 0 ? 'border-t border-slate-100 pt-3 dark:border-slate-800' : ''}>
                  {group.label && (
                    <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{group.label}</p>
                  )}
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = item.icon || FileText;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium ${
                            isActive(item.href)
                              ? 'bg-amber-500 text-white'
                              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                          }`}
                        >
                          <Icon className="h-[18px] w-[18px] shrink-0" />
                          <span className="truncate">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="border-t border-slate-100 pt-3 dark:border-slate-800">
                <button
                  onClick={handleLogout}
                  data-testid="mobile-sidebar-logout-btn"
                  className="flex w-full items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400"
                >
                  <LogOut className="h-[18px] w-[18px]" />
                  Logout
                </button>
              </div>
            </nav>
          </aside>
        </div>
      )}

      {/* Header */}
      <div className="lg:pl-64">
        <div className="glass-header sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-2 px-3 sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="lg:hidden -m-2.5 p-2.5 text-slate-700 dark:text-slate-300"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Global Search */}
          {inArchitectWorkspace ? <div className="flex-1" /> : (
            <form onSubmit={handleSearchSubmit} className="flex min-w-0 flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <div className="relative flex min-w-0 flex-1 max-w-lg">
                <Search className="pointer-events-none absolute inset-y-0 left-3 h-full w-5 text-slate-400" />
                <input
                  type="text"
                  data-testid={SIDEBAR.globalSearch}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search leads by name, mobile, email, city..."
                  className="block w-full min-w-0 border-0 bg-transparent py-0 pl-10 pr-0 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:ring-0 sm:text-sm outline-none"
                />
              </div>
            </form>
          )}

          <div className="flex shrink-0 items-center gap-x-1 sm:gap-x-4 lg:gap-x-6">
            <button
              onClick={toggleDarkMode}
              data-testid="sidebar-theme-toggle"
              className="p-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            {/* Notification Bell */}
            {!inArchitectWorkspace && <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                data-testid={SIDEBAR.notificationBell}
                className="relative p-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Bell className="h-5 w-5" />
                {totalNotifCount > 0 && (
                  <span
                    data-testid={SIDEBAR.notificationBadge}
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-medium flex items-center justify-center"
                  >
                    {totalNotifCount > 99 ? '99+' : totalNotifCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div
                  data-testid={SIDEBAR.notificationDropdown}
                  className="absolute right-0 mt-2 w-96 bg-white dark:bg-[#131A2B] border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-50"
                >
                  <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-outfit font-semibold">Alerts</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {notifData.missed_count} missed · {notifData.today_count} today
                      {(payReminders.counts?.overdue || 0) + (payReminders.counts?.due_today || 0) > 0 &&
                        ` · ${(payReminders.counts?.overdue || 0)} payments overdue`}
                    </p>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {payReminders.counts?.overdue > 0 && (
                      <div className="p-3 border-b border-slate-100 dark:border-slate-800" data-testid="notif-payment-overdue">
                        <p className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> Payments Overdue ({payReminders.counts.overdue})
                        </p>
                        {payReminders.overdue.slice(0, 5).map((p) => (
                          <button
                            key={p.lead_id}
                            onClick={() => { navigate(`/leads/${p.lead_id}?tab=project`); setNotifOpen(false); }}
                            data-testid="notif-payment-item"
                            className="w-full text-left p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg mb-1"
                          >
                            <p className="text-sm font-medium truncate">{p.client} {p.project_serial ? `· ${p.project_serial}` : ''}</p>
                            <p className="text-xs text-muted-foreground">
                              {money(p.amount_due)} due · {p.days_overdue}d overdue{p.payment_stage ? ` · ${p.payment_stage}` : ''}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                    {payReminders.counts?.due_today > 0 && (
                      <div className="p-3 border-b border-slate-100 dark:border-slate-800" data-testid="notif-payment-due">
                        <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <DollarSign className="h-3 w-3" /> Payments Due Today ({payReminders.counts.due_today})
                        </p>
                        {payReminders.due_today.slice(0, 5).map((p) => (
                          <button
                            key={p.lead_id}
                            onClick={() => { navigate(`/leads/${p.lead_id}?tab=project`); setNotifOpen(false); }}
                            data-testid="notif-payment-item"
                            className="w-full text-left p-2 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg mb-1"
                          >
                            <p className="text-sm font-medium truncate">{p.client} {p.project_serial ? `· ${p.project_serial}` : ''}</p>
                            <p className="text-xs text-muted-foreground">
                              {money(p.amount_due)} due today{p.payment_stage ? ` · ${p.payment_stage}` : ''}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                    {notifData.missed_count > 0 && (
                      <div className="p-3 border-b border-slate-100 dark:border-slate-800">
                        <p className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> Missed ({notifData.missed_count})
                        </p>
                        {notifData.missed.map((fu) => (
                          <button
                            key={fu.id}
                            onClick={() => { navigate(`/leads/${fu.lead_id}?tab=followup`); setNotifOpen(false); }}
                            className="w-full text-left p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg mb-1"
                          >
                            <p className="text-sm font-medium truncate">{fu.lead_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {fu.follow_up_type} · {new Date(fu.scheduled_date).toLocaleString()}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                    {notifData.today_count > 0 && (
                      <div className="p-3">
                        <p className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Clock className="h-3 w-3" /> Today ({notifData.today_count})
                        </p>
                        {notifData.today.map((fu) => (
                          <button
                            key={fu.id}
                            onClick={() => { navigate(`/leads/${fu.lead_id}?tab=followup`); setNotifOpen(false); }}
                            className="w-full text-left p-2 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg mb-1"
                          >
                            <p className="text-sm font-medium truncate">{fu.lead_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {fu.follow_up_type} · {new Date(fu.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                    {totalNotifCount === 0 && (
                      <div className="p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
                        <CheckCircle2 className="h-8 w-8 text-green-500" />
                        You're all caught up!
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { navigate('/calendar'); setNotifOpen(false); }}
                    className="w-full p-3 text-sm text-center text-primary hover:bg-slate-50 dark:hover:bg-slate-800 border-t border-slate-200 dark:border-slate-700"
                  >
                    Open Calendar →
                  </button>
                </div>
              )}
            </div>}

            {/* Quick Add — every action reuses an existing CRM destination */}
            <div className="relative" ref={quickRef}>
              <button
                onClick={() => setQuickOpen(!quickOpen)}
                data-testid="quick-add-btn"
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-600"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Quick Add</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {quickOpen && (
                <div
                  data-testid="quick-add-menu"
                  className="absolute right-0 mt-2 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900"
                >
                  {[
                    { key: 'lead', label: 'Add Lead', icon: Users, to: '/leads/new', enabled: hasPermission('leads.create') },
                    ...(hasPermission('projects.create')
                      ? [{ key: 'project', label: 'Create Project', icon: Building2, to: '/projects/new', enabled: true }]
                      : []),
                    { key: 'expense', label: 'Add Expense', icon: Wallet, to: '/finance', enabled: hasPermission('finance.view') },
                  ].map((a) => (
                    <button
                      key={a.key}
                      disabled={!a.enabled}
                      data-testid={`quick-add-${a.key}`}
                      onClick={() => { setQuickOpen(false); navigate(a.to); }}
                      className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <a.icon className="h-4 w-4 text-amber-600" />
                      {a.label}
                    </button>
                  ))}
                  <div className="border-t border-slate-100 px-3 py-2 text-[11px] leading-snug text-slate-400 dark:border-slate-800">
                    Create Task · Upload Drawing · Add Client — no standalone CRM entry point exists yet (awaiting your approval).
                  </div>
                </div>
              )}
            </div>

            {/* Profile / account — reuses existing Settings + Logout */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                data-testid="header-profile-btn"
                className="flex items-center gap-2 rounded-lg px-1.5 py-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">
                  {(user?.name || '?').split(' ').map((w) => w.charAt(0)).slice(0, 2).join('').toUpperCase()}
                </span>
                <span className="hidden text-left leading-tight sm:block">
                  <span className="block max-w-[130px] truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{user?.name}</span>
                  <span className="block text-xs capitalize text-slate-500">{user?.role?.replace(/_/g, ' ')}</span>
                </span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
              {profileOpen && (
                <div
                  data-testid="header-profile-menu"
                  className="absolute right-0 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900"
                >
                  <div className="border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
                    <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{user?.name}</p>
                    <p className="truncate text-xs text-slate-500">{user?.email}</p>
                  </div>
                  {hasPermission('settings.view') && (
                    <button
                      data-testid="profile-menu-settings"
                      onClick={() => { setProfileOpen(false); navigate('/settings'); }}
                      className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <Settings className="h-4 w-4 text-slate-400" /> Settings
                    </button>
                  )}
                  <button
                    data-testid="profile-menu-logout"
                    onClick={() => { setProfileOpen(false); handleLogout(); }}
                    className="flex w-full items-center gap-2.5 border-t border-slate-100 px-3 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 dark:border-slate-800 dark:text-red-400"
                  >
                    <LogOut className="h-4 w-4" /> Logout
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Main content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8">
          {children}
        </main>
      </div>
    </div>
  );
};
